/**
 * Domain vocabulary shared by the API and both UI variants.
 *
 * These arrays are the single source of truth: the Drizzle pgEnum definitions, the Zod
 * request schemas, and the TypeScript unions are all derived from them, so adding a value
 * in one place cannot leave another place behind.
 */

/** Football level. `nfl` and `cfb` (FBS college football). */
export const FOOTBALL_LEVELS = ['nfl', 'cfb'] as const;
export type FootballLevel = (typeof FOOTBALL_LEVELS)[number];

/** Role within a single league. */
export const LEAGUE_ROLES = ['player', 'commissioner'] as const;
export type LeagueRole = (typeof LEAGUE_ROLES)[number];

export const MEMBER_STATUSES = ['active', 'removed'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

export const SEASON_STATUSES = ['upcoming', 'active', 'complete'] as const;
export type SeasonStatus = (typeof SEASON_STATUSES)[number];

/**
 * How the weekly five is drawn.
 *  - nfl_only     : all games from the NFL pool
 *  - cfb_only     : all games from the college pool
 *  - mixed_any    : any random combination across both pools
 *  - mixed_fixed  : an exact split, e.g. 3 NFL + 2 CFB
 *
 * New formats are added here plus one entry in the composition strategy table
 * (apps/api/src/modules/slates/composition.ts). Nothing else needs to change.
 */
export const POOL_MODES = ['nfl_only', 'cfb_only', 'mixed_any', 'mixed_fixed'] as const;
export type PoolMode = (typeof POOL_MODES)[number];

/** When other players' picks become visible. */
export const REVEAL_POLICIES = ['at_kickoff', 'at_slate_lock'] as const;
export type RevealPolicy = (typeof REVEAL_POLICIES)[number];

/**
 * Weekly slate lifecycle.
 *  - draft       : commissioner only; picks rejected; free to reroll and edit
 *  - published   : players may pick unlocked games
 *  - in_progress : at least one game has kicked off
 *  - completed   : every non-voided game is final and graded
 */
export const SLATE_STATUSES = ['draft', 'published', 'in_progress', 'completed'] as const;
export type SlateStatus = (typeof SLATE_STATUSES)[number];

/** Real-world status of a football game, as reported by the data provider. */
export const GAME_STATUSES = [
  'scheduled',
  'in_progress',
  'final',
  'postponed',
  'canceled',
] as const;
export type GameStatus = (typeof GAME_STATUSES)[number];

/**
 * Status of a game *inside a slate*. Superset of GAME_STATUSES: `voided` is a
 * league-level state with no real-world equivalent (the commissioner pulled the game
 * after publication, so it scores nothing and counts for nobody).
 */
export const SLATE_GAME_STATUSES = [...GAME_STATUSES, 'voided'] as const;
export type SlateGameStatus = (typeof SLATE_GAME_STATUSES)[number];

/** Which side of the spread a pick or a result refers to. */
export const SIDES = ['home', 'away'] as const;
export type Side = (typeof SIDES)[number];

export const FAVORITES = ['home', 'away', 'even'] as const;
export type Favorite = (typeof FAVORITES)[number];

/** ATS outcome of a slate game. */
export const ATS_RESULTS = ['pending', 'home_covered', 'away_covered', 'push', 'void'] as const;
export type AtsResult = (typeof ATS_RESULTS)[number];

/** Outcome of one user's pick. */
export const PICK_RESULTS = ['pending', 'win', 'loss', 'push', 'void'] as const;
export type PickResult = (typeof PICK_RESULTS)[number];

/** How a game came to be on the slate. Written on every insert and every swap. */
export const SELECTION_SOURCES = [
  'random',
  'commissioner_manual',
  'commissioner_reroll',
  'commissioner_replace',
] as const;
export type SelectionSource = (typeof SELECTION_SOURCES)[number];

/** Append-only audit log event types. */
export const SLATE_EVENT_TYPES = [
  'generated',
  'reroll_all',
  'reroll_one',
  'replaced',
  'manual_added',
  'removed',
  'voided',
  'reordered',
  'spread_edited',
  'kickoff_edited',
  'published',
  'unpublished',
  'locked',
  'unlocked',
  'graded',
  'grade_corrected',
  'result_cleared',
  'pick_overridden',
] as const;
export type SlateEventType = (typeof SLATE_EVENT_TYPES)[number];

/**
 * Machine-readable error codes. The UI branches on these, never on message text.
 */
export const ERROR_CODES = {
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_COMMISSIONER: 'NOT_COMMISSIONER',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  RATE_LIMITED: 'RATE_LIMITED',
  EMAIL_IN_USE: 'EMAIL_IN_USE',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  INVALID_INVITE: 'INVALID_INVITE',
  INVALID_RESET_TOKEN: 'INVALID_RESET_TOKEN',
  ALREADY_MEMBER: 'ALREADY_MEMBER',
  PICK_LOCKED: 'PICK_LOCKED',
  SLATE_NOT_PUBLISHED: 'SLATE_NOT_PUBLISHED',
  SLATE_ALREADY_PUBLISHED: 'SLATE_ALREADY_PUBLISHED',
  SLATE_INCOMPLETE: 'SLATE_INCOMPLETE',
  SLATE_FULL: 'SLATE_FULL',
  INSUFFICIENT_ELIGIBLE_GAMES: 'INSUFFICIENT_ELIGIBLE_GAMES',
  DUPLICATE_GAME: 'DUPLICATE_GAME',
  GAME_NOT_ELIGIBLE: 'GAME_NOT_ELIGIBLE',
  PICKS_EXIST: 'PICKS_EXIST',
  ACKNOWLEDGEMENT_REQUIRED: 'ACKNOWLEDGEMENT_REQUIRED',
  MISSING_SCORES: 'MISSING_SCORES',
  PROVIDER_ERROR: 'PROVIDER_ERROR',
  INTERNAL: 'INTERNAL',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** Session cookie name. */
export const SESSION_COOKIE = 'pk_session';

/**
 * A game must kick off at least this far in the future to be drawn into a slate.
 * Stops the generator handing the commissioner a game that locks before anyone can pick it.
 */
export const SELECTION_LEAD_TIME_MINUTES = 15;
